import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Plus, Target, ChevronDown, ChevronRight, Trash2, GripVertical,
  Edit2, Check, X, ArrowRightLeft, MoreVertical, Calendar,
} from 'lucide-react';
import {
  DndContext, closestCenter, KeyboardSensor, PointerSensor,
  useSensor, useSensors, type DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove, SortableContext, sortableKeyboardCoordinates,
  useSortable, verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { EmptyState } from '@/components/shared/EmptyState';
import { StatusBadge } from '@/components/shared/StatusBadge';
import { ConfirmDialog } from '@/components/shared/ConfirmDialog';
import { useToast } from '@/hooks/use-toast';
import { sprintService, type SprintGoal, type SprintGoalWithTasks } from '@/services/sprint.service';
import { AttachmentsButton } from './AttachmentsDialog';
import { MoveGoalToSprintDialog } from './MoveGoalToSprintDialog';

interface SprintGoalsPanelProps {
  sprintId: string;
  goals: SprintGoal[];
  canManage: boolean;
  isLocked?: boolean;
  groupId?: string;
}

const phases: Array<{ key: 'business_ready' | 'development_done' | 'testing_completed' | 'uat_approved'; label: string; short: string }> = [
  { key: 'business_ready', label: 'Business Ready', short: 'B' },
  { key: 'development_done', label: 'Development Done', short: 'D' },
  { key: 'testing_completed', label: 'Testing Completed', short: 'T' },
  { key: 'uat_approved', label: 'UAT Approved', short: 'U' },
];

// ── Sortable Goal Item ──

interface SortableGoalItemProps {
  goal: SprintGoalWithTasks;
  canManage: boolean;
  isLocked?: boolean;
  isExpanded: boolean;
  editingGoal: string | null;
  editTitle: string;
  setEditTitle: (v: string) => void;
  onToggleExpand: () => void;
  onStartEdit: () => void;
  onSaveEdit: () => void;
  onCancelEdit: () => void;
  onDelete: () => void;
  onTogglePhase: (field: 'business_ready' | 'development_done' | 'testing_completed' | 'uat_approved') => void;
  onMove: () => void;
}

function SortableGoalItem({
  goal, canManage, isLocked, isExpanded,
  editingGoal, editTitle, setEditTitle,
  onToggleExpand, onStartEdit, onSaveEdit, onCancelEdit, onDelete,
  onTogglePhase, onMove,
}: SortableGoalItemProps) {
  const {
    attributes, listeners, setNodeRef, transform, transition, isDragging,
  } = useSortable({ id: goal.id, disabled: !canManage || isLocked });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const tasks = goal.tasks || [];
  const isEditing = editingGoal === goal.id;
  const allPhasesDone = goal.business_ready && goal.development_done && goal.testing_completed && goal.uat_approved;

  return (
    <Card
      ref={setNodeRef}
      style={style}
      className={`overflow-hidden ${isDragging ? 'shadow-lg z-50' : ''} ${allPhasesDone ? 'bg-muted/50' : ''}`}
    >
      <div className="p-3">
        <div className="flex items-center gap-2">
          {/* Drag handle */}
          {canManage && !isLocked && (
            <GripVertical
              className="h-4 w-4 text-muted-foreground cursor-grab shrink-0 touch-none"
              {...attributes}
              {...listeners}
            />
          )}

          {/* Phase checkboxes */}
          <TooltipProvider delayDuration={0}>
            <div className="flex items-center gap-1 shrink-0">
              {phases.map((p) => (
                <Tooltip key={p.key}>
                  <TooltipTrigger asChild>
                    <button
                      type="button"
                      onClick={() => onTogglePhase(p.key)}
                      disabled={isLocked || !canManage}
                      className={`h-5 w-5 rounded text-[9px] font-bold flex items-center justify-center transition-colors ${
                        goal[p.key]
                          ? 'bg-emerald-500 text-white'
                          : 'bg-muted text-muted-foreground border'
                      } ${canManage && !isLocked ? 'cursor-pointer hover:opacity-80' : ''}`}
                    >
                      {p.short}
                    </button>
                  </TooltipTrigger>
                  <TooltipContent side="top">{p.label}</TooltipContent>
                </Tooltip>
              ))}
              {allPhasesDone && (
                <Check className="h-4 w-4 text-emerald-500 ml-0.5" />
              )}
            </div>
          </TooltipProvider>

          {/* Title - editable or static */}
          {isEditing ? (
            <div className="flex-1 flex items-center gap-1">
              <Input
                value={editTitle}
                onChange={(e) => setEditTitle(e.target.value)}
                className="h-7 text-sm"
                autoFocus
                onKeyDown={(e) => {
                  if (e.key === 'Enter') onSaveEdit();
                  if (e.key === 'Escape') onCancelEdit();
                }}
              />
              <Button size="icon" variant="ghost" className="h-7 w-7 shrink-0" onClick={onSaveEdit}>
                <Check className="h-3.5 w-3.5" />
              </Button>
              <Button size="icon" variant="ghost" className="h-7 w-7 shrink-0" onClick={onCancelEdit}>
                <X className="h-3.5 w-3.5" />
              </Button>
            </div>
          ) : (
            <>
              <span className={`text-sm font-medium flex-1 truncate ${allPhasesDone ? 'line-through text-muted-foreground' : ''}`}>
                {goal.title}
              </span>

              {/* Task count — always visible */}
              <button
                type="button"
                onClick={onToggleExpand}
                className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground transition-colors shrink-0"
              >
                {isExpanded ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
                <span>{goal.completed_tasks}/{goal.total_tasks} tasks</span>
              </button>

              {/* Attachments button */}
              <AttachmentsButton
                entityType="sprint_goal"
                entityId={goal.id}
                title={`${goal.title} - Attachments`}
              />

              {/* Actions menu */}
              {canManage && !isLocked && (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" size="icon" className="h-7 w-7 shrink-0">
                      <MoreVertical className="h-4 w-4" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem onClick={onStartEdit}>
                      <Edit2 className="h-4 w-4 mr-2" />
                      Edit Title
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={onMove}>
                      <ArrowRightLeft className="h-4 w-4 mr-2" />
                      Move to Sprint
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={onDelete} className="text-destructive focus:text-destructive">
                      <Trash2 className="h-4 w-4 mr-2" />
                      Delete
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              )}
            </>
          )}
        </div>
      </div>

      {/* Expanded tasks grouped by stage */}
      {isExpanded && (
        <div className="border-t pt-3 pb-2 px-3 ml-8 mr-2">
          {tasks.length === 0 ? (
            <div className="text-sm text-muted-foreground py-2">No tasks linked to this goal</div>
          ) : (
            <div className="space-y-4">
              {groupTasksByColumn(tasks).map(({ columnName, columnTasks }) => (
                <div key={columnName}>
                  <h4 className="text-xs font-semibold text-muted-foreground mb-2 uppercase tracking-wide border-b pb-1">
                    {columnName} ({columnTasks.length})
                  </h4>
                  <div className="space-y-1">
                    {columnTasks.map((task: any) => (
                      <div
                        key={task.id}
                        className="flex items-center gap-3 p-2 rounded bg-muted/50 text-sm hover:bg-muted transition-colors group"
                      >
                        <span className="font-mono text-xs text-muted-foreground w-20 shrink-0">
                          {task.reference_id || '-'}
                        </span>
                        <span className="flex-1 truncate">
                          {task.title}
                        </span>
                        <span className="text-xs text-muted-foreground w-28 truncate text-center">
                          {task.assignee_name || '-'}
                        </span>
                        <StatusBadge status={task.phase_status} />
                        <StatusBadge status={task.calculated_status} />
                        <span className="text-xs text-muted-foreground w-20 shrink-0 flex items-center gap-1 justify-end">
                          <Calendar className="h-3 w-3" />
                          {task.end_date
                            ? new Date(task.end_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
                            : '-'}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </Card>
  );
}

// ── Main Panel ──

export function SprintGoalsPanel({ sprintId, goals, canManage, isLocked, groupId }: SprintGoalsPanelProps) {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const [createOpen, setCreateOpen] = useState(false);
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [expandedGoals, setExpandedGoals] = useState<Set<string>>(new Set());
  const [deleteGoalId, setDeleteGoalId] = useState<string | null>(null);
  const [editingGoal, setEditingGoal] = useState<string | null>(null);
  const [editTitle, setEditTitle] = useState('');
  const [moveGoal, setMoveGoal] = useState<SprintGoalWithTasks | null>(null);

  // Fetch goals with tasks
  const { data: goalsWithTasks } = useQuery({
    queryKey: ['sprint-goals-tasks', sprintId],
    queryFn: () => sprintService.getGoalsWithTasks(sprintId).then((r) => r.data.data),
    enabled: !!sprintId,
  });

  const enrichedGoals: SprintGoalWithTasks[] = goalsWithTasks ?? goals.map((g) => ({
    ...g,
    total_tasks: 0,
    completed_tasks: 0,
    tasks: [],
  }));

  // DnD sensors
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  function invalidate() {
    queryClient.invalidateQueries({ queryKey: ['sprint', sprintId] });
    queryClient.invalidateQueries({ queryKey: ['sprint-goals-tasks', sprintId] });
  }

  const createMut = useMutation({
    mutationFn: () => sprintService.createGoal(sprintId, { title, description: description || undefined }),
    onSuccess: () => {
      invalidate();
      toast({ title: 'Sprint goal created' });
      setCreateOpen(false); setTitle(''); setDescription('');
    },
    onError: (e: any) => toast({ title: 'Error', description: e.response?.data?.error?.message || 'Failed', variant: 'destructive' }),
  });

  const updateMut = useMutation({
    mutationFn: ({ sgId, data }: { sgId: string; data: Record<string, unknown> }) =>
      sprintService.updateGoal(sprintId, sgId, data),
    onSuccess: () => invalidate(),
    onError: () => toast({ title: 'Update failed', variant: 'destructive' }),
  });

  const deleteMut = useMutation({
    mutationFn: (sgId: string) => sprintService.deleteGoal(sprintId, sgId),
    onSuccess: () => {
      invalidate();
      toast({ title: 'Goal deleted' });
      setDeleteGoalId(null);
    },
    onError: (e: any) => toast({ title: 'Error', description: e.response?.data?.error?.message || 'Failed', variant: 'destructive' }),
  });

  const reorderMut = useMutation({
    mutationFn: (goalIds: string[]) => sprintService.reorderGoals(sprintId, goalIds),
    onSuccess: () => invalidate(),
    onError: () => toast({ title: 'Reorder failed', variant: 'destructive' }),
  });

  function handleDragEnd(event: DragEndEvent) {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const oldIndex = enrichedGoals.findIndex((g) => g.id === active.id);
    const newIndex = enrichedGoals.findIndex((g) => g.id === over.id);
    const reordered = arrayMove(enrichedGoals, oldIndex, newIndex);

    // Optimistic update via queryClient
    queryClient.setQueryData(['sprint-goals-tasks', sprintId], reordered);
    reorderMut.mutate(reordered.map((g) => g.id));
  }

  function togglePhase(sg: SprintGoal, field: 'business_ready' | 'development_done' | 'testing_completed' | 'uat_approved') {
    if (isLocked || !canManage) return;
    updateMut.mutate({ sgId: sg.id, data: { [field]: !sg[field] } });
  }

  function toggleExpand(goalId: string) {
    setExpandedGoals((prev) => {
      const next = new Set(prev);
      if (next.has(goalId)) next.delete(goalId);
      else next.add(goalId);
      return next;
    });
  }

  function handleStartEdit(goal: SprintGoalWithTasks) {
    setEditingGoal(goal.id);
    setEditTitle(goal.title);
  }

  function handleSaveEdit(goalId: string) {
    if (!editTitle.trim()) return;
    updateMut.mutate({ sgId: goalId, data: { title: editTitle.trim() } });
    setEditingGoal(null);
    setEditTitle('');
  }

  function handleCancelEdit() {
    setEditingGoal(null);
    setEditTitle('');
  }

  const completedPhasesTotal = enrichedGoals.reduce((acc, g) => {
    return acc + [g.business_ready, g.development_done, g.testing_completed, g.uat_approved].filter(Boolean).length;
  }, 0);
  const totalPhases = enrichedGoals.length * 4;
  const overallProgress = totalPhases > 0 ? Math.round((completedPhasesTotal / totalPhases) * 100) : 0;

  const goalItems = enrichedGoals.map((g) => g.id);

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Target className="h-5 w-5 text-brand-primary" />
          <h3 className="text-lg font-semibold">Sprint Goals</h3>
          <Badge variant="secondary" className="text-xs">{overallProgress}%</Badge>
        </div>
        {canManage && !isLocked && (
          <Button size="sm" className="bg-brand-primary hover:bg-brand-primary-dark" onClick={() => setCreateOpen(true)}>
            <Plus className="mr-1 h-4 w-4" /> Add Goal
          </Button>
        )}
      </div>

      {enrichedGoals.length === 0 ? (
        <EmptyState icon={Target} title="No sprint goals" description="Add goals to track sprint deliverables." />
      ) : canManage && !isLocked ? (
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
          <SortableContext items={goalItems} strategy={verticalListSortingStrategy}>
            <div className="space-y-2">
              {enrichedGoals.map((sg) => (
                <SortableGoalItem
                  key={sg.id}
                  goal={sg}
                  canManage={canManage}
                  isLocked={isLocked}
                  isExpanded={expandedGoals.has(sg.id)}
                  editingGoal={editingGoal}
                  editTitle={editTitle}
                  setEditTitle={setEditTitle}
                  onToggleExpand={() => toggleExpand(sg.id)}
                  onStartEdit={() => handleStartEdit(sg)}
                  onSaveEdit={() => handleSaveEdit(sg.id)}
                  onCancelEdit={handleCancelEdit}
                  onDelete={() => setDeleteGoalId(sg.id)}
                  onTogglePhase={(field) => togglePhase(sg, field)}
                  onMove={() => setMoveGoal(sg)}
                />
              ))}
            </div>
          </SortableContext>
        </DndContext>
      ) : (
        <div className="space-y-2">
          {enrichedGoals.map((sg) => (
            <SortableGoalItem
              key={sg.id}
              goal={sg}
              canManage={canManage}
              isLocked={isLocked}
              isExpanded={expandedGoals.has(sg.id)}
              editingGoal={editingGoal}
              editTitle={editTitle}
              setEditTitle={setEditTitle}
              onToggleExpand={() => toggleExpand(sg.id)}
              onStartEdit={() => handleStartEdit(sg)}
              onSaveEdit={() => handleSaveEdit(sg.id)}
              onCancelEdit={handleCancelEdit}
              onDelete={() => setDeleteGoalId(sg.id)}
              onTogglePhase={(field) => togglePhase(sg, field)}
              onMove={() => setMoveGoal(sg)}
            />
          ))}
        </div>
      )}

      {/* Add new goal inline */}
      {canManage && !isLocked && enrichedGoals.length > 0 && (
        <button
          type="button"
          onClick={() => setCreateOpen(true)}
          className="w-full text-left text-sm text-muted-foreground hover:text-foreground px-3 py-2 rounded-lg border border-dashed hover:border-brand-primary transition-colors"
        >
          Add a new goal...
        </button>
      )}

      {/* Create dialog */}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="sm:max-w-[400px]">
          <DialogHeader>
            <DialogTitle>Add Sprint Goal</DialogTitle>
            <DialogDescription>Define a deliverable for this sprint.</DialogDescription>
          </DialogHeader>
          <form onSubmit={(e) => { e.preventDefault(); createMut.mutate(); }} className="space-y-4">
            <div className="space-y-2">
              <Label>Title *</Label>
              <Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Sprint goal title" required />
            </div>
            <div className="space-y-2">
              <Label>Description</Label>
              <textarea
                className="flex min-h-[60px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm resize-y"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Optional description"
              />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setCreateOpen(false)}>Cancel</Button>
              <Button type="submit" className="bg-brand-primary hover:bg-brand-primary-dark" disabled={!title.trim() || createMut.isPending}>
                {createMut.isPending ? 'Creating...' : 'Add'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete confirmation */}
      <ConfirmDialog
        open={!!deleteGoalId}
        onOpenChange={() => setDeleteGoalId(null)}
        title="Delete Sprint Goal"
        description="Are you sure you want to delete this sprint goal?"
        confirmLabel="Delete"
        variant="destructive"
        isLoading={deleteMut.isPending}
        onConfirm={() => deleteGoalId && deleteMut.mutate(deleteGoalId)}
      />

      {/* Move goal dialog */}
      {moveGoal && groupId && (
        <MoveGoalToSprintDialog
          open={!!moveGoal}
          onOpenChange={(open) => !open && setMoveGoal(null)}
          goal={moveGoal}
          currentSprintId={sprintId}
          groupId={groupId}
        />
      )}
    </div>
  );
}

function groupTasksByColumn(tasks: any[]): Array<{ columnName: string; columnTasks: any[] }> {
  const groups: Record<string, { order: number; tasks: any[] }> = {};
  for (const t of tasks) {
    const name = t.column_name || 'Unassigned';
    if (!groups[name]) groups[name] = { order: t.column_order ?? 999, tasks: [] };
    groups[name].tasks.push(t);
  }
  return Object.entries(groups)
    .sort(([, a], [, b]) => a.order - b.order)
    .map(([name, data]) => ({ columnName: name, columnTasks: data.tasks }));
}
